import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useNotificationPreferences } from "@/hooks/useNotificationPreferences";
import { Loader2 } from "lucide-react";

const NOTIFICATION_TYPES = [
  {
    category: "Task Notifications",
    types: [
      { id: "task_assigned", label: "Task assigned to you" },
      { id: "task_due_soon", label: "Task due soon (24-48 hours)" },
      { id: "task_overdue", label: "Task overdue" },
      { id: "task_suggestion", label: "AI suggested task from email" },
    ],
  },
  {
    category: "Opportunity Notifications",
    types: [
      { id: "opportunity_won", label: "Opportunity won 🎉" },
      { id: "opportunity_stage_changed", label: "Opportunity moved to new stage" },
      { id: "opportunity_suggestion", label: "AI suggested opportunity from email" },
    ],
  },
  {
    category: "Activity Notifications",
    types: [
      { id: "comment_added", label: "New comment on your items" },
    ],
  },
];

export const NotificationPreferencesSettings = () => {
  const { preferences, isLoading, updatePreferences, toggleNotificationType, isUpdating } = useNotificationPreferences();
  
  const [localPrefs, setLocalPrefs] = useState({
    in_app_enabled: true,
    priority_threshold: 'low' as 'low' | 'medium' | 'high' | 'critical',
    auto_mark_read_after_days: 7,
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00',
    quiet_hours_end: '08:00',
  });

  useEffect(() => {
    if (preferences) {
      setLocalPrefs({
        in_app_enabled: preferences.in_app_enabled,
        priority_threshold: preferences.priority_threshold,
        auto_mark_read_after_days: preferences.auto_mark_read_after_days,
        quiet_hours_enabled: preferences.quiet_hours_enabled,
        quiet_hours_start: preferences.quiet_hours_start || '22:00',
        quiet_hours_end: preferences.quiet_hours_end || '08:00',
      });
    }
  }, [preferences]);

  const handleSave = () => {
    updatePreferences(localPrefs);
  };

  const isTypeEnabled = (typeId: string) => {
    if (!preferences) return true;
    return preferences.enabled_notification_types.includes(typeId);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Notification Preferences</CardTitle>
          <CardDescription>Loading your preferences...</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notification Preferences</CardTitle>
        <CardDescription>
          Customize what notifications you receive and when
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* General Settings */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">General Settings</h3>
          
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="in-app">Enable in-app notifications</Label>
              <p className="text-sm text-muted-foreground">
                Show notifications within the application
              </p>
            </div>
            <Switch
              id="in-app"
              checked={localPrefs.in_app_enabled}
              onCheckedChange={(checked) => 
                setLocalPrefs({ ...localPrefs, in_app_enabled: checked })
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="priority">Priority threshold</Label>
            <Select
              value={localPrefs.priority_threshold}
              onValueChange={(value: any) => 
                setLocalPrefs({ ...localPrefs, priority_threshold: value })
              }
            >
              <SelectTrigger id="priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">All notifications</SelectItem>
                <SelectItem value="medium">Medium priority and above</SelectItem>
                <SelectItem value="high">High priority and above</SelectItem>
                <SelectItem value="critical">Critical only</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Only show notifications at or above this priority level
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="auto-mark">Auto-mark as read after (days)</Label>
            <Input
              id="auto-mark"
              type="number"
              min="1"
              max="30"
              value={localPrefs.auto_mark_read_after_days}
              onChange={(e) => 
                setLocalPrefs({ ...localPrefs, auto_mark_read_after_days: parseInt(e.target.value) || 7 })
              }
            />
          </div>
        </div>

        <Separator />

        {/* Notification Types */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Notification Types</h3>
          
          {NOTIFICATION_TYPES.map((category) => (
            <div key={category.category} className="space-y-3">
              <h4 className="font-medium text-sm text-muted-foreground">
                {category.category}
              </h4>
              {category.types.map((type) => (
                <div key={type.id} className="flex items-center justify-between pl-4">
                  <Label htmlFor={type.id} className="font-normal cursor-pointer">
                    {type.label}
                  </Label>
                  <Switch
                    id={type.id}
                    checked={isTypeEnabled(type.id)}
                    onCheckedChange={(checked) => 
                      toggleNotificationType({ type: type.id, enabled: checked })
                    }
                  />
                </div>
              ))}
            </div>
          ))}
        </div>

        <Separator />

        {/* Quiet Hours */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Quiet Hours</h3>
          
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="quiet-hours">Enable quiet hours</Label>
              <p className="text-sm text-muted-foreground">
                Pause notifications during specific hours
              </p>
            </div>
            <Switch
              id="quiet-hours"
              checked={localPrefs.quiet_hours_enabled}
              onCheckedChange={(checked) => 
                setLocalPrefs({ ...localPrefs, quiet_hours_enabled: checked })
              }
            />
          </div>

          {localPrefs.quiet_hours_enabled && (
            <div className="grid grid-cols-2 gap-4 pl-4">
              <div className="space-y-2">
                <Label htmlFor="quiet-start">Start time</Label>
                <Input
                  id="quiet-start"
                  type="time"
                  value={localPrefs.quiet_hours_start}
                  onChange={(e) => 
                    setLocalPrefs({ ...localPrefs, quiet_hours_start: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quiet-end">End time</Label>
                <Input
                  id="quiet-end"
                  type="time"
                  value={localPrefs.quiet_hours_end}
                  onChange={(e) => 
                    setLocalPrefs({ ...localPrefs, quiet_hours_end: e.target.value })
                  }
                />
              </div>
            </div>
          )}
        </div>

        <Separator />

        {/* Future Features */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Delivery Channels</h3>
          
          <div className="flex items-center justify-between opacity-50">
            <div className="space-y-0.5">
              <Label>Email notifications</Label>
              <p className="text-sm text-muted-foreground">
                Receive notifications via email
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">Coming Soon</Badge>
              <Switch disabled />
            </div>
          </div>

          <div className="flex items-center justify-between opacity-50">
            <div className="space-y-0.5">
              <Label>Push notifications</Label>
              <p className="text-sm text-muted-foreground">
                Browser push notifications
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">Coming Soon</Badge>
              <Switch disabled />
            </div>
          </div>
        </div>

        <Separator />

        {/* Save Button */}
        <div className="flex items-center justify-between pt-4">
          <p className="text-sm text-muted-foreground">
            {preferences && `Last updated: ${new Date(preferences.updated_at).toLocaleString()}`}
          </p>
          <Button onClick={handleSave} disabled={isUpdating}>
            {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Preferences
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
